import { Box, Divider, HStack, Heading, Pressable, VStack } from 'native-base'

import { Icon, PencilSimple, Plus } from '@phosphor-icons/react'

import { colors } from '../../../utils/theme/configureTheme'

interface AddEditBoxProps {
  label: string
  children: React.ReactNode
  ContentIcon: Icon
  canEdit: boolean
  onEdit?: () => void
}

/** Labelled info box with an optional edit button, used to display and modify order address details. */
export default function AddEditBox({ label, children, ContentIcon, canEdit, onEdit }: AddEditBoxProps) {
  // These pressables are icon-only, so without an explicit label they reach the DOM as
  // a role-less div with no accessible name — unusable for screen readers and for
  // role-based test locators alike. react-native-web maps accessibilityRole/Label onto
  // role and aria-label.
  const editLabel = `Edit ${label}`
  const addLabel = `Add ${label}`
  const handleEditClick = () => {
    if (onEdit) {
      onEdit()
    }
  }

  return (
    <Box p={3} borderWidth={'1px'} borderColor={colors.brand.lightGrey} borderRadius={8}>
      {canEdit ? (
        <VStack space={2}>
          {/* Header */}
          <HStack justifyContent="space-between" alignItems="center">
            <HStack alignItems="center" space={2}>
              <Box
                width={10}
                height={10}
                borderRadius="full"
                bg={colors.brand.lightGrey}
                justifyContent="center"
                alignItems="center"
              >
                <ContentIcon size={24} color={colors.brand.darkGrey} weight="duotone" />
              </Box>
              <Heading fontSize={10} fontWeight={'700'} fontFamily={'bodyBold'}>
                {`EDIT ${label.toUpperCase()}`}
              </Heading>
            </HStack>
            <Pressable onPress={handleEditClick} accessible accessibilityRole="button" accessibilityLabel={editLabel}>
              <PencilSimple size={24} color={colors.brand.softBlack} weight="bold" />
            </Pressable>
          </HStack>

          <Divider color={colors.brand.lightGrey} />

          {children}
        </VStack>
      ) : (
        <HStack justifyContent="space-between" alignItems="center">
          <HStack alignItems="center" space={2}>
            <Box
              width={10}
              height={10}
              borderRadius="full"
              bg={colors.brand.lightGrey}
              justifyContent="center"
              alignItems="center"
            >
              <ContentIcon size={24} color={colors.brand.darkGrey} weight="duotone" />
            </Box>
            <Heading fontSize={10} fontWeight={'700'} fontFamily={'bodyBold'}>
              {`ADD ${label.toUpperCase()}`}
            </Heading>
          </HStack>
          <Pressable onPress={handleEditClick} accessible accessibilityRole="button" accessibilityLabel={addLabel}>
            <Plus size={24} color={colors.brand.softBlack} weight="bold" />
          </Pressable>
        </HStack>
      )}
    </Box>
  )
}
